// MSAL auth + shared API caller. Formerly the inline <script> in index.html.
// Exports IT_CONFIG / IT_AUTH / API_BASE, and also bridges them onto window
// so the many existing `window.IT_AUTH.api(...)` call sites keep working
// unchanged. msal itself stays a CDN global (SRI-pinned in index.html).
/* global msal */

export const IT_CONFIG = {
  TENANT_ID:          '8d7d683c-ca76-4d33-8230-c5a4db4bdc25',
  CLIENT_ID:          'e2e22cf7-d952-45b9-b8e1-f67b6a94ae06',
  ALL_STAFF_GROUP_ID: '2050c5ac-ec7c-43a9-9ceb-2f1ce4f4b325',
};
window.IT_CONFIG = IT_CONFIG;

const _msalInstance = new msal.PublicClientApplication({
  auth: {
    clientId: IT_CONFIG.CLIENT_ID,
    authority: `https://login.microsoftonline.com/${IT_CONFIG.TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  // sessionStorage instead of localStorage. Tradeoff: users re-authenticate
  // every browser session (closes when the last Triage tab is closed),
  // gained: tokens DO NOT survive an XSS bypass of DOMPurify or
  // decodeEntities - attacker can't grab tokens from sessionStorage if the
  // user has already closed the tab. Tokens still expire normally
  // (~1 hour for the access token) within a session.
  cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: false },
});

export const API_BASE = 'https://it-ticketing-api-dcgwgjerducxg8ck.uksouth-01.azurewebsites.net/api';

export const IT_AUTH = {
  // Shared API caller used by every view. Acquires a bearer token via MSAL
  // then fetches /api/<path>. Easy Auth validates the token server-side.
  // The `label` is just for console warnings so callers can distinguish
  // their origin in the dev console.
  async api(method, path, body, label = 'api') {
    let authHeader = {};
    try {
      const token = await this.getApiToken();
      authHeader = { Authorization: `Bearer ${token}` };
    } catch (e) {
      console.warn(`[${label}] no token available for ${method} ${path} -`, e.message);
    }
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeader },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (res.status === 204 || res.headers.get('content-length') === '0') return null;
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `${method} ${path} → ${res.status}`);
    return data;
  },
  async signIn() {
    await _msalInstance.initialize();
    // Redirect flow (not popup) because the sessionStorage cache (June 2026)
    // means parent and popup windows have *separate* storage - popup mode
    // breaks when MSAL tries to share auth state across them. Redirect uses
    // the same tab so sessionStorage works. Page navigates away here - the
    // response is processed by handleRedirectPromise() in init() on return.
    await _msalInstance.loginRedirect({
      scopes: ['User.Read', 'User.ReadBasic.All', 'GroupMember.Read.All', `api://${IT_CONFIG.CLIENT_ID}/access_as_user`],
    });
    // Unreachable - loginRedirect navigates the tab. Kept for type-symmetry.
  },
  // Silent SSO: sign in using the browser's existing Microsoft 365 session
  // via a hidden iframe - no UI, no click. Throws (e.g. InteractionRequired)
  // if the session can't be used silently; callers fall back to signIn().
  async ssoSilent() {
    await _msalInstance.initialize();
    const result = await _msalInstance.ssoSilent({
      scopes: ['User.Read', 'User.ReadBasic.All', 'GroupMember.Read.All', `api://${IT_CONFIG.CLIENT_ID}/access_as_user`],
    });
    if (result?.account) _msalInstance.setActiveAccount(result.account);
    return result;
  },
  async getToken() {
    await _msalInstance.initialize();
    const account = _msalInstance.getActiveAccount() || (_msalInstance.getAllAccounts()[0] || null);
    if (!account) throw new Error('Not signed in');
    const result = await _msalInstance.acquireTokenSilent({ scopes: ['User.Read', 'User.ReadBasic.All', 'GroupMember.Read.All'], account });
    return result.accessToken;
  },
  async getStaff() {
    // sessionStorage cache. Graph enumeration of ALL_STAFF group returns
    // ~100 people and takes 200-500ms - worth skipping on every reload
    // within the same tab session. Cache invalidates when the tab closes
    // (session-scoped storage), so a leaver/joiner shows correctly on
    // the next fresh session; overkill to invalidate mid-session.
    const cached = sessionStorage.getItem('triage:staff-cache-v1');
    if (cached) {
      try { return JSON.parse(cached); } catch (_) { /* fall through and refetch */ }
    }
    const token = await this.getToken();
    let url = `https://graph.microsoft.com/v1.0/groups/${IT_CONFIG.ALL_STAFF_GROUP_ID}/members?$top=999&$select=id,displayName,mail,userPrincipalName,jobTitle,department`;
    const members = [];
    while (url) {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || 'Could not fetch staff list');
      members.push(...(data.value || []));
      url = data['@odata.nextLink'] || null;
    }
    const list = members
      .filter(m => m.displayName)
      .map(m => ({ ...m, mail: m.mail || m.userPrincipalName }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
    try { sessionStorage.setItem('triage:staff-cache-v1', JSON.stringify(list)); } catch (_) { /* quota */ }
    return list;
  },
  async getITTeamProfiles(emails) {
    // Fetch IT team job titles directly by UPN - works regardless of group membership.
    // sessionStorage cache: 5 separate Graph calls in parallel, each 50-100ms,
    // all to look up job titles that don't change day-to-day. No need to
    // redo them on every reload within the same tab session.
    const cached = sessionStorage.getItem('triage:it-profiles-cache-v1');
    if (cached) {
      try { return JSON.parse(cached); } catch (_) { /* fall through */ }
    }
    const token = await this.getToken();
    const results = await Promise.all(emails.map(async email => {
      try {
        const res = await fetch(
          `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}?$select=mail,userPrincipalName,jobTitle`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return null;
        return res.json();
      } catch { return null; }
    }));
    const list = results.filter(Boolean);
    try { sessionStorage.setItem('triage:it-profiles-cache-v1', JSON.stringify(list)); } catch (_) { /* quota */ }
    return list;
  },
  async getCurrentUser() {
    const token = await this.getToken();
    const res = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Could not fetch user profile');
    return res.json();
  },
  async checkITMember() {
    // sessionStorage cache - once we've confirmed the user is IT this session,
    // no need to re-enumerate every group they're in on each reload. Cache
    // is per-tab; a fresh tab re-checks, so a removed user is locked out on
    // their next session (worst-case delay = one tab close).
    const cached = sessionStorage.getItem('triage:isIT-cache-v1');
    if (cached === 'true')  return true;
    if (cached === 'false') return false;
    const token = await this.getToken();
    // Use TRANSITIVE membership so nested groups count. Matters when
    // '~ALL IT' (the on-prem synced group) is added as a nested member
    // of a cloud-only wrapper group (like 'Triage IT Admins') so we can
    // also add cloud-only service accounts to the wrapper group without
    // touching on-prem AD.
    const res = await fetch('https://graph.microsoft.com/v1.0/me/transitiveMemberOf?$select=id&$top=999', {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    // Accept membership of either the on-prem '~ALL IT' group OR the new
    // cloud-only wrapper group (whose ID gets added here once you create
    // it - send me the Object ID and I'll add it below).
    const IT_GROUP_IDS = [
      '67b216a0-b335-4d49-bbd4-d434bae4d611', // ~ALL IT (on-prem synced)
      '87bd6ea3-bf96-413d-9ba3-4def55854458', // Triage IT Admins (cloud-only wrapper - includes ~ALL IT as nested + service account as direct)
    ];
    // Debug log so we can see in the browser console exactly what groups
    // came back and whether our check matched - helps diagnose deploy /
    // cache issues. Safe to leave in permanently; noisy in console but
    // no PII exposed (just group IDs).
    const matchedGroups = (data.value || []).filter(g => IT_GROUP_IDS.includes(g.id));
    console.log('[isIT check]',
      'user has', (data.value || []).length, 'transitive groups.',
      'matched:', matchedGroups.map(g => g.id));
    const isIT = (data.value || []).some(g => IT_GROUP_IDS.includes(g.id));
    try { sessionStorage.setItem('triage:isIT-cache-v1', String(isIT)); } catch (_) { /* quota */ }
    return isIT;
  },
  async getApiToken() {
    await _msalInstance.initialize();
    const account = _msalInstance.getActiveAccount() || (_msalInstance.getAllAccounts()[0] || null);
    if (!account) throw new Error('Not signed in');
    const scopes = [`api://${IT_CONFIG.CLIENT_ID}/access_as_user`];
    try {
      const result = await _msalInstance.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch {
      // Silent acquisition failed (token expired, refresh failed) - fall
      // back to interactive redirect. Same reasoning as loginRedirect
      // above: popup variants break with sessionStorage cache.
      await _msalInstance.acquireTokenRedirect({ scopes, account });
      // Unreachable - redirect navigates away.
    }
  },
  async signOut() {
    await _msalInstance.initialize();
    // Redirect-based sign-out to match the redirect-based sign-in. The
    // popup variant has the same sessionStorage isolation problem.
    await _msalInstance.logoutRedirect();
  },
  async init() {
    await _msalInstance.initialize();
    // Process any pending redirect response (from a just-completed
    // loginRedirect). Returns the auth result if we just came back from
    // Microsoft sign-in, null on normal page loads. Sets the active
    // account so subsequent token acquires work without re-auth.
    try {
      const result = await _msalInstance.handleRedirectPromise();
      if (result?.account) _msalInstance.setActiveAccount(result.account);
    } catch (e) {
      console.error('[IT_AUTH] handleRedirectPromise failed:', e);
    }
    const accounts = _msalInstance.getAllAccounts();
    if (accounts.length > 0 && !_msalInstance.getActiveAccount()) {
      _msalInstance.setActiveAccount(accounts[0]);
    }
    return _msalInstance.getActiveAccount() != null;
  },
  getActiveAccount() {
    return _msalInstance.getActiveAccount() || (_msalInstance.getAllAccounts()[0] || null);
  },
};
window.IT_AUTH = IT_AUTH;
